import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Box, useMantineColorScheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import Sidebar from './Sidebar';
import TopHeader from './TopHeader';
import { authService } from '../services/authService';
import { profileService } from '../services/profileService';
import { UsuarioProfile } from '../types/profile';
import PageTransition from './animations/PageTransition';
import { SidebarProvider } from '../contexts/SidebarContext';
import { queryClient } from '../lib/queryClient';
import {
  especialidadesQuery,
  temasQuery,
  dashboardStatsQuery,
  flashcardStatsQuery,
  rankingQuery,
  perfilQuery,
  adminUserStatisticsQuery,
  adminSubscriptionPlansQuery,
  adminEspecialidadesQuery,
  adminTemasQuery,
  adminDificultadesQuery,
  adminReactivosPageQuery,
} from '../queries/appQueries';

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { colorScheme } = useMantineColorScheme();
  const [user, setUser] = useState(authService.getCurrentUserFromStorage());
  const [profile, setProfile] = useState<UsuarioProfile | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Móvil / iPad vertical (<1024px): el sidebar es overlay (no empuja el contenido).
  // iPad horizontal (1024px) y escritorio mantienen el empuje.
  const isMobile = useMediaQuery('(max-width: 1023px)', false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAdmin = user?.roles?.includes('ROLE_ADMIN') || user?.roles?.includes('ADMIN');

  // Load user profile on mount
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profileData = await profileService.getMyProfile();
        setProfile(profileData);
      } catch (error) {
        console.error('Error loading profile:', error);
        // Si no se puede cargar el perfil, continuar sin él
      }
    };

    if (authService.isAuthenticated()) {
      // Re-leemos el usuario de storage por si el Layout montó antes de que el
      // login terminara de guardarlo: así `isAdmin` (y el ancho del sidebar) se
      // corrigen en cuanto el usuario está disponible, en vez de quedar congelados.
      const fresh = authService.getCurrentUserFromStorage();
      if (fresh && fresh.id !== user?.id) setUser(fresh);
      loadProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefetch en segundo plano de los datasets más usados, para que navegar
  // entre páginas sea instantáneo (los datos ya estarán en caché de React Query).
  useEffect(() => {
    if (!authService.isAuthenticated()) return;

    // Releemos el usuario fresco de storage en vez de usar el `user` congelado
    // del useState: justo tras el login el Layout puede montar antes de que el
    // usuario esté en localStorage, y el estado nunca se actualiza. Así evitamos
    // que el prefetch se salte o use el set equivocado (admin vs estudiante).
    const current = authService.getCurrentUserFromStorage();
    const admin = current?.roles?.some((r: string) => r === 'ROLE_ADMIN' || r === 'ADMIN');

    if (admin) {
      // Admin: prefetch lo que se comparte entre páginas pesadas.
      queryClient.prefetchQuery(adminUserStatisticsQuery());
      queryClient.prefetchQuery(adminSubscriptionPlansQuery());
      queryClient.prefetchQuery(adminEspecialidadesQuery());
      queryClient.prefetchQuery(adminTemasQuery());
      queryClient.prefetchQuery(adminDificultadesQuery());
      // La key debe coincidir EXACTO con el primer render de QuestionDatabasePage para que sea cache-hit.
      queryClient.prefetchQuery(adminReactivosPageQuery({
        page: 0, size: 10, especialidadId: null, temaId: null, dificultadId: null, q: '',
      }));
      return;
    }

    queryClient.prefetchQuery(especialidadesQuery());
    queryClient.prefetchQuery(temasQuery());
    queryClient.prefetchQuery(dashboardStatsQuery());
    queryClient.prefetchQuery(rankingQuery());
    if (current?.id) {
      queryClient.prefetchQuery(flashcardStatsQuery(current.id));
      queryClient.prefetchQuery(perfilQuery(current.id));
    }
  }, []);

  // Auto-collapse sidebar on specific pages (e.g., settings, exam-taking, results)
  useEffect(() => {
    const collapsePrefixes = [
      '/settings',
      '/estudiante/simulador/rapido',
      '/estudiante/simulador/filtros',
      '/estudiante/simulador/completo',
      '/estudiante/examen-filtrado',
      '/estudiante/examen-rapido',
      '/estudiante/resultados/',
      '/examenes/',
    ];
    if (collapsePrefixes.some((p) => location.pathname.startsWith(p))) {
      setSidebarCollapsed(true);
    } else {
      setSidebarCollapsed(false);
    }
  }, [location.pathname]);

  // Cerrar el sidebar overlay al navegar (en móvil)
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate('/login');
    } catch (error) {
      console.error('Error en logout:', error);
      navigate('/login');
    }
  };

  return (
    <SidebarProvider>
    <Box
      style={{
        minHeight: '100vh',
        background: colorScheme === 'dark'
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
          : 'linear-gradient(135deg, #f7f3ee 0%, #f2ede6 100%)',
        display: 'flex',
      }}
    >
      {/* Sidebar */}
      <Sidebar
        user={{
          username: user?.username || '',
          email: user?.email || '',
          roles: user?.roles || [],
          nombre: profile?.nombre || user?.nombre,
          apellidos: profile?.apellido || user?.apellidos,
          avatar: profile?.avatar
        }}
        onLogout={handleLogout}
        onCollapseChange={setSidebarCollapsed}
        initialCollapsed={sidebarCollapsed}
        isMobile={isMobile}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      {/* Backdrop del sidebar en móvil */}
      {isMobile && mobileOpen && (
        <Box
          onClick={() => setMobileOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.45)',
            zIndex: 1050,
          }}
        />
      )}

      {/* Right Side Container */}
      <Box
        style={{
          marginLeft: isMobile ? 0 : (sidebarCollapsed ? '60px' : (isAdmin ? '260px' : '220px')),
          flex: 1,
          minWidth: 0,
          transition: 'margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
        }}
      >
        {/* Top Header */}
        <TopHeader
          user={{
            username: user?.username || '',
            email: user?.email || '',
            roles: user?.roles || [],
            nombre: user?.nombre,
            apellidos: user?.apellidos,
          }}
          onLogout={handleLogout}
          sidebarWidth={0}
          isMobile={isMobile}
          onMenuClick={() => setMobileOpen(true)}
        />

        {/* Main Content */}
        <Box
          style={{
            flex: 1,
            overflow: 'hidden',
            overflowY: 'auto',
          }}
        >
          <PageTransition type="medical" duration={800}>
            <Outlet />
          </PageTransition>
        </Box>
      </Box>
    </Box>
    </SidebarProvider>
  );
};

export default Layout;
